import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  IdcardOutlined,
  MergeCellsOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import { PageHeader } from "../../../components/common/PageHeader";
import { usePageTitle } from "../../../hooks";

const { Text } = Typography;

interface BusinessPosition {
  id: number;
  name: string;
  name_en: string;
  name_it: string;
  name_pt: string;
  assignment_count: number;
}

interface BusinessPositionFormValues {
  name: string;
  name_en?: string;
  name_it?: string;
  name_pt?: string;
}

/**
 * Admin page for `BusinessPosition` — the catalogue of "cargos" that
 * can be assigned to producer contacts. Supports:
 *   - CRUD with a guard that blocks deletion when the position is in
 *     use (i.e. assignment_count > 0).
 *   - Bulk merge: select N positions, pick one as the destination,
 *     and the backend reassigns every contact reference to the
 *     target before deleting the sources.
 */
export const BusinessPositionsList = () => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();
  const [data, setData] = useState<BusinessPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  // Edit / create modal
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<BusinessPosition | null>(null);
  const [form] = Form.useForm<BusinessPositionFormValues>();
  const [saving, setSaving] = useState(false);

  // Merge modal
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<number | null>(null);
  const [merging, setMerging] = useState(false);

  usePageTitle(t("business_positions.title", "Cargos de contacto"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<BusinessPosition[]>("/business-positions/");
      // Endpoint is non-paginated — Refine-style {results:[…]} fallback
      // included to be safe.
      const list = Array.isArray(r.data)
        ? r.data
        : ((r.data as any).results ?? []);
      setData(list);
    } catch {
      notify.error(t("business_positions.load_error", "Error al cargar los cargos"));
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // ----- CRUD ---------------------------------------------------------

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setEditorOpen(true);
  };

  const openEdit = (row: BusinessPosition) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      name_en: row.name_en ?? "",
      name_it: row.name_it ?? "",
      name_pt: row.name_pt ?? "",
    });
    setEditorOpen(true);
  };

  const submitEditor = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await apiClient.patch(`/business-positions/${editing.id}/`, values);
        notify.success(
          t("business_positions.update_success", "Cargo actualizado"),
        );
      } else {
        await apiClient.post("/business-positions/", values);
        notify.success(t("business_positions.create_success", "Cargo creado"));
      }
      setEditorOpen(false);
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        e?.response?.data?.name?.[0] ??
        t("business_positions.save_error", "No se pudo guardar el cargo");
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const deletePosition = async (row: BusinessPosition) => {
    try {
      await apiClient.delete(`/business-positions/${row.id}/`);
      notify.success(t("business_positions.delete_success", "Cargo eliminado"));
      setSelectedRowKeys((keys) => keys.filter((k) => k !== row.id));
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t("business_positions.delete_error", "No se pudo eliminar el cargo");
      notify.error(msg);
    }
  };

  // ----- merge --------------------------------------------------------

  const selectedRows = useMemo(
    () => data.filter((r) => selectedRowKeys.includes(r.id)),
    [data, selectedRowKeys],
  );

  const openMerge = () => {
    if (selectedRowKeys.length < 2) {
      notify.warning(
        t(
          "business_positions.merge_need_two",
          "Selecciona al menos dos cargos para fusionar.",
        ),
      );
      return;
    }
    // Default the merge target to the row with the most assignments —
    // usually what the admin wants to keep.
    const heaviest = [...selectedRows].sort(
      (a, b) => b.assignment_count - a.assignment_count,
    )[0];
    setMergeTargetId(heaviest?.id ?? selectedRowKeys[0]);
    setMergeOpen(true);
  };

  const submitMerge = async () => {
    if (!mergeTargetId) return;
    const sources = selectedRowKeys.filter((k) => k !== mergeTargetId);
    if (sources.length === 0) {
      notify.warning(
        t(
          "business_positions.merge_no_sources",
          "Selecciona al menos un cargo distinto del destino.",
        ),
      );
      return;
    }
    setMerging(true);
    try {
      const res = await apiClient.post<{
        moved_producer_contacts: number;
        moved_legacy_contacts: number;
      }>("/business-positions/merge/", {
        target_id: mergeTargetId,
        source_ids: sources,
      });
      const moved =
        (res.data?.moved_producer_contacts ?? 0) +
        (res.data?.moved_legacy_contacts ?? 0);
      notify.success(
        t(
          "business_positions.merge_success",
          "Fusionados {{n}} cargos · {{moved}} contactos reasignados",
          { n: sources.length, moved },
        ),
      );
      setMergeOpen(false);
      setSelectedRowKeys([]);
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t("business_positions.merge_error", "No se pudo fusionar");
      notify.error(msg);
    } finally {
      setMerging(false);
    }
  };

  // ----- table --------------------------------------------------------

  const columns = [
    {
      title: t("business_positions.name", "Nombre"),
      dataIndex: "name",
      key: "name",
      ellipsis: { showTitle: false } as any,
      render: (v: string) => (
        <Tooltip title={v} placement="topLeft" mouseEnterDelay={0.25}>
          <strong>{v}</strong>
        </Tooltip>
      ),
      sorter: (a: BusinessPosition, b: BusinessPosition) =>
        a.name.localeCompare(b.name),
    },
    {
      title: t("business_positions.translations", "Traducciones"),
      key: "translations",
      width: 220,
      render: (_: any, row: BusinessPosition) => {
        // Show one chip per available translation. Missing ones are
        // greyed out so the admin can spot incomplete entries at
        // a glance.
        const entries: Array<{ code: string; label: string; value: string }> = [
          { code: "EN", label: "English", value: row.name_en },
          { code: "IT", label: "Italiano", value: row.name_it },
          { code: "PT", label: "Português", value: row.name_pt },
        ];
        return (
          <Space size={4} wrap>
            {entries.map((e) =>
              e.value ? (
                <Tooltip key={e.code} title={`${e.label}: ${e.value}`}>
                  <Tag color="blue" style={{ margin: 0 }}>{e.code}</Tag>
                </Tooltip>
              ) : (
                <Tooltip
                  key={e.code}
                  title={`${e.label}: ${t("business_positions.no_translation", "sin traducir")}`}
                >
                  <Tag color="default" style={{ margin: 0, opacity: 0.55 }}>
                    {e.code}
                  </Tag>
                </Tooltip>
              ),
            )}
          </Space>
        );
      },
    },
    {
      title: t("business_positions.assignment_count", "Contactos asignados"),
      dataIndex: "assignment_count",
      key: "assignment_count",
      width: 180,
      align: "center" as const,
      sorter: (a: BusinessPosition, b: BusinessPosition) =>
        a.assignment_count - b.assignment_count,
      render: (n: number) =>
        n > 0 ? (
          <Tag color="blue">{n}</Tag>
        ) : (
          <Tag color="default">0</Tag>
        ),
    },
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 160,
      align: "right" as const,
      render: (_: any, row: BusinessPosition) => (
        <Space size="small">
          <Tooltip title={t("common.edit", "Editar")}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row)}
            />
          </Tooltip>
          <Popconfirm
            title={t(
              "business_positions.delete_confirm",
              "¿Eliminar este cargo?",
            )}
            description={
              row.assignment_count > 0
                ? t(
                    "business_positions.delete_blocked",
                    "No puedes eliminar un cargo asignado. Fusiónalo primero.",
                  )
                : t(
                    "business_positions.delete_warning",
                    "Esta acción no se puede deshacer.",
                  )
            }
            onConfirm={() => deletePosition(row)}
            okText={t("common.yes", "Sí")}
            cancelText={t("common.no", "No")}
            okButtonProps={{ danger: true }}
            disabled={row.assignment_count > 0}
          >
            <Tooltip
              title={
                row.assignment_count > 0
                  ? t(
                      "business_positions.delete_blocked_tooltip",
                      "Tiene contactos asignados — no se puede eliminar",
                    )
                  : t("common.delete", "Eliminar")
              }
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={row.assignment_count > 0}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <PageHeader
        icon={<IdcardOutlined />}
        accent="#13C2C2"
        title={t("business_positions.title", "Cargos de contacto")}
        subtitle={t(
          "business_positions.subtitle",
          "Catálogo de cargos asignables a los contactos de productores. Fusiona los duplicados para mantener la lista limpia.",
        )}
        extra={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              {t("common.reload", "Refrescar")}
            </Button>
            {/* Always clickable so the user can discover why it's not
                doing anything yet. If <2 rows are checked, openMerge
                surfaces a clear hint instead of staying greyed-out. */}
            <Tooltip
              title={
                selectedRowKeys.length < 2
                  ? t(
                      "business_positions.merge_hint_tooltip",
                      "Marca las casillas de 2 o más cargos en la tabla para poder fusionarlos",
                    )
                  : ""
              }
            >
              <Button
                icon={<MergeCellsOutlined />}
                onClick={openMerge}
                type={selectedRowKeys.length >= 2 ? "primary" : "default"}
              >
                {t("business_positions.merge", "Fusionar")}
                {selectedRowKeys.length > 0 && ` (${selectedRowKeys.length})`}
              </Button>
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("business_positions.create", "Nuevo cargo")}
            </Button>
          </Space>
        }
      />

      {/* Inline hint banner — appears until the user has marked
          enough rows. Hides itself once they've selected 2+ so it
          doesn't keep nagging. */}
      {selectedRowKeys.length < 2 && (
        <Alert
          showIcon
          type="info"
          style={{ marginBottom: 12 }}
          message={t(
            "business_positions.merge_banner",
            "Cómo fusionar: marca las casillas (☐) de los cargos duplicados en la tabla y pulsa Fusionar. Podrás elegir cuál mantener y los contactos del resto se reasignarán automáticamente.",
          )}
        />
      )}

      <Table<BusinessPosition>
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 960 }}
        rowSelection={{
          // Explicit type so antd never falls back to a no-selection
          // column on stale CSS or version mismatches.
          type: "checkbox",
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as number[]),
          getCheckboxProps: () => ({ disabled: false }),
          columnWidth: 48,
          fixed: "left",
        }}
      />

      {/* Create / Edit modal */}
      <Modal
        title={
          editing
            ? t("business_positions.edit_title", "Editar cargo")
            : t("business_positions.create_title", "Nuevo cargo")
        }
        open={editorOpen}
        onCancel={() => (saving ? undefined : setEditorOpen(false))}
        onOk={submitEditor}
        confirmLoading={saving}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t("business_positions.name_es", "Nombre (Español)")}
            name="name"
            rules={[
              {
                required: true,
                message: t(
                  "business_positions.name_required",
                  "El nombre es obligatorio",
                ),
              },
              { max: 255 },
            ]}
            extra={t(
              "business_positions.name_es_hint",
              "Idioma principal del catálogo — siempre obligatorio.",
            )}
          >
            <Input
              maxLength={255}
              placeholder={t(
                "business_positions.name_placeholder",
                "Ej: Gerente de finca",
              )}
              autoFocus
            />
          </Form.Item>

          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4, marginBottom: 8 }}>
            {t(
              "business_positions.translations_hint",
              "Traducciones opcionales — se usan en los exports de Excel y en la UI según el idioma del usuario.",
            )}
          </Text>

          <Form.Item
            label={t("business_positions.name_en", "Nombre (English)")}
            name="name_en"
            rules={[{ max: 255 }]}
          >
            <Input
              maxLength={255}
              placeholder={t(
                "business_positions.name_en_placeholder",
                "e.g. Farm Manager",
              )}
            />
          </Form.Item>

          <Form.Item
            label={t("business_positions.name_it", "Nome (Italiano)")}
            name="name_it"
            rules={[{ max: 255 }]}
          >
            <Input
              maxLength={255}
              placeholder={t(
                "business_positions.name_it_placeholder",
                "es. Gestore aziendale",
              )}
            />
          </Form.Item>

          <Form.Item
            label={t("business_positions.name_pt", "Nome (Português)")}
            name="name_pt"
            rules={[{ max: 255 }]}
          >
            <Input
              maxLength={255}
              placeholder={t(
                "business_positions.name_pt_placeholder",
                "ex: Gerente de fazenda",
              )}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Merge modal */}
      <Modal
        title={t("business_positions.merge_title", "Fusionar cargos")}
        open={mergeOpen}
        onCancel={() => (merging ? undefined : setMergeOpen(false))}
        onOk={submitMerge}
        confirmLoading={merging}
        okText={t("business_positions.merge_apply", "Fusionar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        okButtonProps={{ danger: true }}
        width={520}
      >
        <Text type="secondary">
          {t(
            "business_positions.merge_hint",
            "Selecciona el cargo que quieres mantener. Los demás se eliminarán y todos sus contactos se reasignarán al cargo elegido.",
          )}
        </Text>
        <div style={{ marginTop: 12 }}>
          <Radio.Group
            value={mergeTargetId}
            onChange={(e) => setMergeTargetId(e.target.value)}
          >
            <Space direction="vertical">
              {selectedRows.map((row) => (
                <Radio key={row.id} value={row.id}>
                  <strong>{row.name}</strong>{" "}
                  <Text type="secondary">
                    ({row.assignment_count}{" "}
                    {row.assignment_count === 1
                      ? t("business_positions.contact_one", "contacto")
                      : t("business_positions.contact_many", "contactos")})
                  </Text>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
      </Modal>
    </Card>
  );
};

export default BusinessPositionsList;
